import type {
  AmhsLoadPort,
  AmhsRunRequest,
  AmhsStocker,
  AmhsTool,
} from '../../types/amhsSimulationRequest';
import {
  AmhsRequestValidationError,
  buildAmhsRunRequest,
  type BuildAmhsRunRequestInput,
} from './buildAmhsRunRequest';

function toolPortIssues(tool: AmhsTool, ports: AmhsLoadPort[]): string | null {
  const label = tool.name || tool.toolId;
  const loads = ports.filter((port) => port.portKind === 'LOAD').length;
  const unloads = ports.filter((port) => port.portKind === 'UNLOAD').length;
  const bayIntrabay = ports.filter((port) => port.portKind === 'Intrabay').length;
  const intrabay = ports.filter((port) => port.bayType === 'intrabay').length;
  if (ports.length === 2 && loads === 1 && unloads === 1 && intrabay === 2) return null;
  // transportFlow-off mode: portKind carries bay classification.
  if (ports.length === 2 && bayIntrabay === 2) return null;
  return `Equipment "${label}" needs 2 intrabay docks: 1 LOAD + 1 UNLOAD`;
}

function stockerPortIssues(stocker: AmhsStocker, ports: AmhsLoadPort[]): string | null {
  const label = stocker.name || stocker.stockerId;
  const interbay = ports.filter((port) => port.portKind === 'Interbay').length;
  const intrabay = ports.filter((port) => port.portKind === 'Intrabay').length;
  const raw = ports.filter((port) => port.columnKind === 'raw').length;
  const processed = ports.filter((port) => port.columnKind === 'processed').length;
  const tagged = ports.every(
    (port) =>
      (port.columnKind === 'raw' || port.columnKind === 'processed') &&
      (port.inventoryLevel === 'stocker' || port.inventoryLevel === 'bay')
  );
  if (
    ports.length === 4 &&
    interbay === 2 &&
    intrabay === 2 &&
    raw === 2 &&
    processed === 2 &&
    tagged
  ) {
    return null;
  }
  return `Stocker "${label}" needs 4 docks: 2 Interbay + 2 Intrabay, each tagged raw/processed with inventoryLevel`;
}

/** Non-blocking reminders shown in the Simulation panel checklist. */
export const AMHS_RUN_ADVISORY_NOTES = [
  'Check track arrow directions — travel should flow the way you intend',
  'Make sure track nodes sit in front of each equipment and stocker port so OHTs can dock',
] as const;

export type AmhsRunReadiness = {
  /** Layout checks shown in the Simulation panel (advisory — do not block Run). */
  issues: string[];
  /** Soft reminders — never block Run. */
  notes: string[];
};

/**
 * Validate a built `/amhs/run` request for simulation readiness.
 * Returns human-readable issues; empty means the payload is ready to send.
 */
export function validateAmhsRunPayload(request: AmhsRunRequest): string[] {
  const issues: string[] = [];
  const stockers: AmhsStocker[] = request.stockers?.stockers ?? [];
  const tools: AmhsTool[] = request.tools?.tools ?? [];
  const loadPorts: AmhsLoadPort[] = request.loadPorts?.loadPorts ?? [];

  if (!stockers.some((stocker) => stocker.outsideStocker === true)) {
    issues.push('Mark one stocker as Outside Stocker in Properties');
  }

  for (const tool of tools) {
    const ports = loadPorts.filter((port) => port.toolId === tool.toolId);
    const issue = toolPortIssues(tool, ports);
    if (issue) issues.push(issue);
  }

  for (const stocker of stockers) {
    const ports = loadPorts.filter((port) => port.stockerId === stocker.stockerId);
    const issue = stockerPortIssues(stocker, ports);
    if (issue) issues.push(issue);
  }

  const hasEntry =
    Boolean(request.transportFlow?.entryLandmarkId) ||
    loadPorts.some((port) => port.portKind === 'ENTRY_GATE');
  const hasExit =
    Boolean(request.transportFlow?.exitLandmarkId) ||
    loadPorts.some((port) => port.portKind === 'EXIT_GATE');

  if (!hasEntry) {
    issues.push('Define an Entry node (select a track node → Type: Entry)');
  }
  if (!hasExit) {
    issues.push('Define an Exit node (select a track node → Type: Exit)');
  }

  return issues;
}

/**
 * Build the AMHS run payload (when possible) and collect readiness issues + notes
 * for the Simulation panel. Notes never block Run.
 */
export function collectAmhsRunReadiness(
  input: BuildAmhsRunRequestInput
): AmhsRunReadiness {
  const notes = [...AMHS_RUN_ADVISORY_NOTES];
  try {
    const request = buildAmhsRunRequest(input);
    return { issues: validateAmhsRunPayload(request), notes };
  } catch (error) {
    if (error instanceof AmhsRequestValidationError) {
      return {
        issues: error.issues.map((issue) =>
          issue.startsWith('add ') ? `Add ${issue.slice(4)}` : issue
        ),
        notes,
      };
    }
    return {
      issues: [
        error instanceof Error ? error.message : 'Cannot build simulation request',
      ],
      notes,
    };
  }
}

/** @deprecated Prefer {@link collectAmhsRunReadiness}; kept for older call sites. */
export function collectAmhsRunReadinessIssues(
  input: BuildAmhsRunRequestInput
): string[] {
  return collectAmhsRunReadiness(input).issues;
}
